import { c as _c } from "react/compiler-runtime";
import { useGetAIPromptConfigQuery } from '@repo/store/maps/api/mapsApi';
import type { AIProvider } from './use-map-text-suggestion';
const PROMPT_KEYS = {
  title: 'map_title_suggestion',
  description: 'map_description_suggestion'
} as const;
const PROMPT_CONFIG_REFETCH_TTL_SECONDS = 300;
type PromptField = keyof typeof PROMPT_KEYS;
interface ResolvedPromptConfig {
  provider?: AIProvider;
  model?: string;
  systemPrompt?: string;
  maxTokens?: number;
}
const FALLBACK_SYSTEM_PROMPTS: Record<PromptField, string> = {
  title: ['Rewrite the text as a simple, polished title for an interactive game map pin.', 'Use title case when it fits naturally.', 'Keep it concise.', 'Preserve the original meaning.', 'Return only the title text.'].join(' '),
  description: ['Rewrite the description for an interactive game map pin to fix grammar and spelling while preserving the original meaning and Markdown formatting.', 'Keep the tone casual and natural.', 'Gamer slang and informal wording are okay when they fit the content.', 'Do not make it sound overly formal.', 'Do not use em dashes.', 'Return only the rewritten description.'].join(' ')
};
function parseModelString(modelString: string | undefined): {
  provider: AIProvider;
  model: string;
} | undefined {
  if (!modelString) return undefined;
  if (modelString.includes('@')) {
    const atIndex = modelString.indexOf('@');
    const provider = modelString.slice(0, atIndex);
    const model = modelString.slice(atIndex + 1);
    if (!['openai', 'anthropic', 'mock'].includes(provider) || !model) {
      return undefined;
    }
    return {
      provider: provider as AIProvider,
      model
    };
  }
  if (modelString.startsWith('gpt-') || modelString.startsWith('o1') || modelString.startsWith('o3')) {
    return {
      provider: 'openai',
      model: modelString
    };
  }
  if (modelString.startsWith('claude-')) {
    return {
      provider: 'anthropic',
      model: modelString
    };
  }
  if (modelString === 'mock') {
    return {
      provider: 'mock',
      model: modelString
    };
  }
  return {
    provider: 'openai',
    model: 'gpt-5-mini'
  };
}
export function useAIPromptConfig(field, titleId) {
  const $ = _c(11);
  const t0 = PROMPT_KEYS[field];
  let t1;
  if ($[0] !== t0 || $[1] !== titleId) {
    t1 = {
      key: t0,
      titleId
    };
    $[0] = t0;
    $[1] = titleId;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      refetchOnMountOrArgChange: PROMPT_CONFIG_REFETCH_TTL_SECONDS
    };
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const {
    data
  } = useGetAIPromptConfigQuery(t1, t2);
  const t3 = data?.model;
  let t4;
  if ($[4] !== t3) {
    t4 = parseModelString(t3);
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const modelConfig = t4;
  const t5 = modelConfig?.provider;
  const t6 = modelConfig?.model;
  const t7 = data?.instructions || FALLBACK_SYSTEM_PROMPTS[field];
  const t8 = data?.maxTokens;
  let t9;
  if ($[6] !== t5 || $[7] !== t6 || $[8] !== t7 || $[9] !== t8) {
    t9 = {
      provider: t5,
      model: t6,
      systemPrompt: t7,
      maxTokens: t8
    };
    $[6] = t5;
    $[7] = t6;
    $[8] = t7;
    $[9] = t8;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  return t9;
}